/**
 * Centralized TanStack Query keys. Keep all keys here so cache invalidations
 * are explicit and discoverable.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
    onboarding: ['auth', 'onboarding'] as const,
    brandKit: ['auth', 'brandKit'] as const,
  },
  profile: {
    me: ['profile'] as const,
    activity: (type: string, page: number) =>
      ['profile', 'activity', type, page] as const,
  },
  plans: {
    all: ['plans'] as const,
    current: ['plans', 'current'] as const,
    usage: ['plans', 'usage'] as const,
    invoices: ['plans', 'invoices'] as const,
  },
  companies: {
    all: ['companies'] as const,
    me: ['companies', 'me'] as const,
    detail: (id: number) => ['companies', id] as const,
    managers: (id: number) => ['companies', id, 'managers'] as const,
  },
  brands: {
    all: ['brands'] as const,
    detail: (id: number) => ['brands', id] as const,
  },
  brandInventory: {
    all: ['brand-inventory'] as const,
    list: (brandId?: number, state?: string, tag?: string) =>
      ['brand-inventory', 'list', { brandId, state, tag }] as const,
    detail: (id: number) => ['brand-inventory', id] as const,
  },
  // "Projects" in the UI map to event groups. Keys are scoped by brand.
  eventGroups: {
    all: ['event-groups'] as const,
    byBrand: (brandId: number) => ['event-groups', { brandId }] as const,
    detail: (id: number) => ['event-groups', id] as const,
    // MEASURE tab — project-wide stock allocation report.
    report: (id: number) => ['event-groups', id, 'report'] as const,
  },
  // Reporters — event-scoped list + the brand-wide distinct list that powers
  // the Planner "reporters you've worked with" panel.
  reporters: {
    byEvent: (eventId: number) => ['reporters', { eventId }] as const,
    byBrand: (brandId: number) => ['reporters', { brandId }] as const,
  },
  // Shared-QR groups (several stations behind one QR).
  qrGroups: {
    byEvent: (eventId: number) => ['qr-groups', { eventId }] as const,
    publicDetail: (id: number) => ['qr-groups', 'public', id] as const,
  },
  stationBrandConfig: {
    byStation: (stationId: number) => ['station-brand-config', { stationId }] as const,
    detail: (id: number) => ['station-brand-config', id] as const,
  },
  bsEvent: {
    byEvent: (eventId: number) => ['bs-event', { eventId }] as const,
    detail: (id: number) => ['bs-event', id] as const,
  },
  events: {
    all: ['events'] as const,
    byBrand: (brandId: number) => ['events', { brandId }] as const,
    byGroup: (groupId: number) => ['events', { groupId }] as const,
    detail: (id: number) => ['events', id] as const,
  },
  stations: {
    all: ['stations'] as const,
    byBrand: (brandId: number) => ['stations', { brandId }] as const,
    byEvent: (eventId: number) => ['stations', { eventId }] as const,
    detail: (id: number) => ['stations', id] as const,
    summary: (id: number) => ['stations', id, 'summary'] as const,
    responses: (id: number, page: number) =>
      ['stations', id, 'responses', page] as const,
  },
  stock: {
    all: ['stock'] as const,
    byBrand: (brandId: number) => ['stock', { brandId }] as const,
    detail: (id: number) => ['stock', id] as const,
  },
  stockAllocations: {
    byBrand: (brandId: number) => ['stock-allocations', { brandId }] as const,
    byStation: (stationId: number) => ['stock-allocations', { stationId }] as const,
    byEvent: (eventId: number) => ['stock-allocations', { eventId }] as const,
  },
  surveys: {
    all: ['surveys'] as const,
    templates: ['surveys', 'templates'] as const,
    detail: (id: number) => ['surveys', id] as const,
    preview: (id: number) => ['surveys', id, 'preview'] as const,
    publicObject: (surveyId: string, stationId: string) =>
      ['surveys', 'public', surveyId, stationId] as const,
  },
  contacts: {
    // Caso 01 — Leads CRM. The `all` key is the legacy mock key; new
    // queries scope by filter snapshot so list invalidations don't blow
    // away the detail cache.
    all: ['contacts'] as const,
    list: (filters: Record<string, unknown>) =>
      ['contacts', 'list', filters] as const,
    detail: (id: number) => ['contacts', 'detail', id] as const,
  },
  favorites: {
    all: ['favorites'] as const,
    byBrand: (brandId: number) => ['favorites', { brandId }] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    inbox: ['notifications', 'inbox'] as const,
    unread: ['notifications', 'unread'] as const,
    sent: ['notifications', 'sent'] as const,
    candidates: ['notifications', 'candidates'] as const,
  },
  // Xamplin domain (xp-*) — build-v2 F2 Home command center + planner.
  xamplin: {
    projects: (companyId?: number) =>
      ['xamplin', 'projects', { companyId: companyId ?? null }] as const,
    projectStructure: (id: number) =>
      ['xamplin', 'project', id, 'structure'] as const,
    assetPurposes: ['xamplin', 'asset-purposes'] as const,
    assets: ['xamplin', 'assets'] as const,
    measure: (id: number) => ['xamplin', 'measure', id] as const,
    eventMeasure: (id: number) => ['xamplin', 'event-measure', id] as const,
    photoFeed: (filters: Record<string, unknown>) =>
      ['xamplin', 'photo-feed', filters] as const,
    photoComments: (id: number) => ['xamplin', 'photo', id, 'comments'] as const,
    photoReactions: (id: number) => ['xamplin', 'photo', id, 'reactions'] as const,
    warehouses: ['xamplin', 'warehouses'] as const,
    brandKit: (companyId?: number, brandId?: number) =>
      [
        'xamplin',
        'brandkit',
        { companyId: companyId ?? null, brandId: brandId ?? null },
      ] as const,
  },
} as const;
